/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect } from "react";
import FormFader from "../Common/Fader/FormFader";
import wizardStyle from "../wizardStyle";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import Hidden from "@mui/material/Hidden";
import AlimonyPerson from "./Alimony/AlimonyPerson";
import { useForm } from "../../../provider/Form/FormProvider";
import { Box, MenuItem } from "@mui/material";
import Group from "../../Wizard/Group";
import { oneOfTheseOptionsValidator, requiredValidator } from "../../../services/validationRules";
import ValidatorSelectField from "../../FormFields/ValidatorSelectField";
import _cloneDeep from "lodash/cloneDeep";
import _slice from "lodash/slice";
import { addMessage } from "../../../store/messages/actions";
import _toNumber from "lodash/toNumber";
import Typography from "@mui/material/Typography";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import FormSubmitProps from "../../../types/FormSubmitProps";
import { Person } from "../../../types/Entities/Person";

type AlimonyProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
};

export default function Alimony({ product, submitProps }: AlimonyProps) {
  const { values, handleChange, handleSubmit } = useForm();

  useEffect(() => {
    const numberOfAlimonyPersons = values.numberOfAlimonyPersons ? _toNumber(values.numberOfAlimonyPersons) : 0;
    if (values.alimonyPersons.length === numberOfAlimonyPersons) {
      return;
    }
    const emptyPerson = {
      gender: "",
      givenName: "",
      familyName: "",
      livesWithPetitioner: false,
      address: {
        streetAddress: "",
        postalCode: "",
        addressLocality: "",
      },
      birthDate: "",
      relation: "",
      alimony: "",
      hasIncome: "",
      income: "",
    };
    let alimonyPersons = _cloneDeep(values.alimonyPersons);
    for (let i = alimonyPersons.length; i < Math.min(numberOfAlimonyPersons, 5); i++) {
      alimonyPersons.push(_cloneDeep(emptyPerson));
    }
    alimonyPersons = _slice(alimonyPersons, 0, numberOfAlimonyPersons);
    handleChange({
      target: {
        type: "other",
        name: "alimonyPersons",
        value: alimonyPersons,
      },
    });
  }, [values.alimonyPersons, values.numberOfAlimonyPersons]);

  useEffect(() => {
    if (values.hasRelativesAlimonyClaims === "no" && product.divorce.numberChildren > 0) {
      addMessage({
        type: "warning",
        text: "Sie haben zuvor angegeben, dass Sie minderjährige Kinder haben. Wenn Sie Ihren Kindern Unterhalt zahlen, oder diese bei Ihnen leben und sie sie versorgen, geben Sie das hier unbedingt an. Die Wahrscheinlichkeit, dass Sie Verfahrenskostenhilfe erhalten, ist dann deutlich höher! ",
      });
    }
  }, [values.hasRelativesAlimonyClaims]);

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={wizardStyle.wizardForm} onSubmit={handleSubmit}>
          <Hidden smUp>
            <Typography variant={"h4"} sx={wizardStyle.headline}>
              Persönliche Angaben
            </Typography>
          </Hidden>
          <Grid container justifyContent={"center"}>
            <Grid item xs={12}>
              <Group
                type={"radio"}
                name={"hasRelativesAlimonyClaims"}
                question={"Müssen Sie an jemanden Unterhalt zahlen, oder leben Kinder bei Ihnen, die Sie versorgen?"}
                modalContent={
                  <p>
                    Bitte geben Sie an, wenn <strong>Sie</strong> jemandem Unterhalt zahlen oder Kinder bei Ihnen leben und durch Sie versorgt werden. Es ist
                    dabei nicht wichtig, ob Sie einen monatlichen Geldbetrag zahlen oder den Unterhalt dadurch leisten, dass diese Person bei Ihnen wohnt und
                    von Ihnen versorgt wird. In beiden Fällen ist die Angabe sehr wichtig, da es die Chance auf Verfahrenskostenhilfe erhöht.
                  </p>
                }
                options={[
                  {
                    labelText: "Nein",
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                  {
                    labelText: "Ja",
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.hasRelativesAlimonyClaims === "yes" && (
              <>
                <ValidatorSelectField
                  label="Wie vielen Personen zahlen Sie Unterhalt?"
                  name="numberOfAlimonyPersons"
                  validators={[oneOfTheseOptionsValidator(["1", "2", "3", "4", "5"], "Bitte geben Sie an wie vielen Personen Sie unterhalt zahlen")]}
                >
                  <MenuItem value={"1"}>1</MenuItem>
                  <MenuItem value={"2"}>2</MenuItem>
                  <MenuItem value={"3"}>3</MenuItem>
                  <MenuItem value={"4"}>4</MenuItem>
                  <MenuItem value={"5"}>5</MenuItem>
                </ValidatorSelectField>
                {values.alimonyPersons?.map((alimonyPerson: Person, index: number) => (
                  <AlimonyPerson key={index} index={index} path={`alimonyPersons[${index}]`} product={product} />
                ))}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
